/**
 * Merges per-environment usage summaries into the single view the page renders.
 *
 * Pure, so the de-duplication and derivation rules can be tested without a
 * connected environment.
 *
 * @module usageMerge
 */
import type {
  EnvironmentId,
  UsageBucket,
  UsageTier,
  UsageSourceFingerprint,
  UsageSummary,
} from "@amoreg/contracts";

export interface EnvironmentUsage {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly summary: UsageSummary;
}

export interface TierTotals {
  readonly tier: UsageTier;
  readonly costGbp: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly costShare: number;
  readonly tokenShare: number;
}

export interface ModelTotals {
  readonly model: string;
  readonly tier: UsageTier;
  readonly costGbp: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly costShare: number;
}

/** Spend by who made the calls: the lead agent (`agent: null`) or one delegated helper. */
export interface AgentTotals {
  readonly agent: string | null;
  readonly costGbp: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly costShare: number;
}

export interface DailyTotals {
  readonly day: string;
  readonly costGbp: number;
  readonly totalTokens: number;
  readonly byTier: ReadonlyMap<UsageTier, { costGbp: number; totalTokens: number }>;
}

export interface CostQuality {
  readonly gatewayBilledShare: number;
  readonly modelPricedShare: number;
  readonly unpricedShare: number;
  readonly cacheSavingsGbp: number;
}

export interface MergedUsage {
  readonly costGbp: number;
  readonly uncachedInputTokens: number;
  readonly cachedInputTokens: number;
  readonly cacheCreationTokens: number;
  readonly outputTokens: number;
  readonly reasoningTokens: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly sessions: number;
  readonly tiers: readonly TierTotals[];
  readonly models: readonly ModelTotals[];
  readonly agents: readonly AgentTotals[];
  readonly daily: readonly DailyTotals[];
  readonly costQuality: CostQuality;
  /** Environments whose data was dropped as a duplicate of another's. */
  readonly duplicateSources: readonly string[];
  readonly contributingEnvironments: readonly EnvironmentId[];
  readonly staleEnvironments: readonly EnvironmentId[];
}

/**
 * Two sources are the same ledger only when the database and the tenant scope
 * both agree.
 *
 * The tenant is part of the identity on purpose: two environments on one
 * database scoped to different organizations hold genuinely different data,
 * and collapsing them would silently drop one tenant's spend.
 */
function fingerprintKey(fingerprint: UsageSourceFingerprint): string {
  return `${fingerprint.databaseId} ${fingerprint.organizationScope}`;
}

/**
 * Decides which environment owns each ledger.
 *
 * Several environments can point at one Amoreg database — a worktree server
 * beside the primary, or a shared dev stack — and would otherwise report the
 * same generations twice. The first environment in a stable order claims a
 * fingerprint; the rest are dropped. Environments are sorted by id so the
 * winner does not change between renders.
 */
function claimSources(environments: readonly EnvironmentUsage[]): {
  readonly ownerByFingerprint: ReadonlyMap<string, EnvironmentId>;
  readonly duplicates: readonly string[];
} {
  const ownerByFingerprint = new Map<string, EnvironmentId>();
  const duplicates: string[] = [];

  const ordered = [...environments].sort((a, b) => a.environmentId.localeCompare(b.environmentId));

  for (const environment of ordered) {
    for (const source of environment.summary.sources) {
      if (source.status === "missing") continue;
      const key = fingerprintKey(source.fingerprint);
      if (ownerByFingerprint.has(key)) {
        duplicates.push(`${environment.label}: ${source.fingerprint.databaseId}`);
        continue;
      }
      ownerByFingerprint.set(key, environment.environmentId);
    }
  }

  return { ownerByFingerprint, duplicates };
}

/**
 * What this environment contributes after fingerprint claims.
 *
 * An environment whose every source was claimed by another contributes
 * nothing: its buckets are the same generations the owner already reported.
 */
function ownedContribution(
  environment: EnvironmentUsage,
  ownerByFingerprint: ReadonlyMap<string, EnvironmentId>,
): { readonly buckets: readonly UsageBucket[]; readonly sessions: number } {
  let owned = false;
  let sessions = 0;
  for (const source of environment.summary.sources) {
    if (source.status === "missing") continue;
    if (ownerByFingerprint.get(fingerprintKey(source.fingerprint)) === environment.environmentId) {
      owned = true;
      // Distinct within a ledger. Summing per-bucket session counts instead
      // would count a conversation once per day and model it spans.
      sessions += source.distinctSessions;
    }
  }
  return { buckets: owned ? environment.summary.buckets : [], sessions };
}

function bucketTokens(bucket: UsageBucket): number {
  // reasoningTokens is a subset of outputTokens and must not be added again.
  return (
    bucket.totals.uncachedInputTokens +
    bucket.totals.cachedInputTokens +
    bucket.totals.cacheCreationTokens +
    bucket.totals.outputTokens
  );
}

const EMPTY_MERGED: MergedUsage = {
  costGbp: 0,
  uncachedInputTokens: 0,
  cachedInputTokens: 0,
  cacheCreationTokens: 0,
  outputTokens: 0,
  reasoningTokens: 0,
  totalTokens: 0,
  records: 0,
  sessions: 0,
  tiers: [],
  models: [],
  agents: [],
  daily: [],
  costQuality: {
    gatewayBilledShare: 0,
    modelPricedShare: 0,
    unpricedShare: 0,
    cacheSavingsGbp: 0,
  },
  duplicateSources: [],
  contributingEnvironments: [],
  staleEnvironments: [],
};

/**
 * Merges every connected environment's summary.
 *
 * `expectedContractVersion` guards against an environment running older server
 * code: rather than blocking the page, its data is excluded and its id is
 * reported so the UI can say coverage is partial.
 */
export function mergeUsage(
  environments: readonly EnvironmentUsage[],
  expectedContractVersion: number,
): MergedUsage {
  if (environments.length === 0) return EMPTY_MERGED;

  const current: EnvironmentUsage[] = [];
  const staleEnvironments: EnvironmentId[] = [];
  for (const environment of environments) {
    if (environment.summary.contractVersion === expectedContractVersion) {
      current.push(environment);
    } else {
      staleEnvironments.push(environment.environmentId);
    }
  }

  const { ownerByFingerprint, duplicates } = claimSources(current);

  let costGbp = 0;
  let uncachedInputTokens = 0;
  let cachedInputTokens = 0;
  let cacheCreationTokens = 0;
  let outputTokens = 0;
  let reasoningTokens = 0;
  let records = 0;
  let sessions = 0;
  let cacheSavingsGbp = 0;
  let gatewayBilledRecords = 0;
  let unpricedRecords = 0;

  const tierAccumulator = new Map<
    UsageTier,
    { costGbp: number; totalTokens: number; records: number }
  >();
  const modelAccumulator = new Map<
    string,
    { tier: UsageTier; costGbp: number; totalTokens: number; records: number }
  >();
  const agentAccumulator = new Map<
    string | null,
    { costGbp: number; totalTokens: number; records: number }
  >();
  const dailyAccumulator = new Map<
    string,
    {
      costGbp: number;
      totalTokens: number;
      byTier: Map<UsageTier, { costGbp: number; totalTokens: number }>;
    }
  >();
  const contributingEnvironments: EnvironmentId[] = [];

  for (const environment of current) {
    const { buckets, sessions: environmentSessions } = ownedContribution(
      environment,
      ownerByFingerprint,
    );
    if (buckets.length > 0) contributingEnvironments.push(environment.environmentId);
    sessions += environmentSessions;

    for (const bucket of buckets) {
      const tokens = bucketTokens(bucket);

      costGbp += bucket.costGbp;
      cacheSavingsGbp += bucket.cacheSavingsGbp;
      uncachedInputTokens += bucket.totals.uncachedInputTokens;
      cachedInputTokens += bucket.totals.cachedInputTokens;
      cacheCreationTokens += bucket.totals.cacheCreationTokens;
      outputTokens += bucket.totals.outputTokens;
      reasoningTokens += bucket.totals.reasoningTokens;
      records += bucket.records;
      unpricedRecords += bucket.unpricedRecords;
      if (bucket.costSource === "gatewayBilled") gatewayBilledRecords += bucket.records;

      const tier = tierAccumulator.get(bucket.tier) ?? {
        costGbp: 0,
        totalTokens: 0,
        records: 0,
      };
      tier.costGbp += bucket.costGbp;
      tier.totalTokens += tokens;
      tier.records += bucket.records;
      tierAccumulator.set(bucket.tier, tier);

      const modelKey = JSON.stringify([bucket.tier, bucket.model]);
      const model = modelAccumulator.get(modelKey) ?? {
        tier: bucket.tier,
        costGbp: 0,
        totalTokens: 0,
        records: 0,
      };
      model.costGbp += bucket.costGbp;
      model.totalTokens += tokens;
      model.records += bucket.records;
      modelAccumulator.set(modelKey, model);

      const agentKey = bucket.agent ?? null;
      const agent = agentAccumulator.get(agentKey) ?? { costGbp: 0, totalTokens: 0, records: 0 };
      agent.costGbp += bucket.costGbp;
      agent.totalTokens += tokens;
      agent.records += bucket.records;
      agentAccumulator.set(agentKey, agent);

      const day = dailyAccumulator.get(bucket.day) ?? {
        costGbp: 0,
        totalTokens: 0,
        byTier: new Map<UsageTier, { costGbp: number; totalTokens: number }>(),
      };
      day.costGbp += bucket.costGbp;
      day.totalTokens += tokens;
      const dayTier = day.byTier.get(bucket.tier) ?? { costGbp: 0, totalTokens: 0 };
      dayTier.costGbp += bucket.costGbp;
      dayTier.totalTokens += tokens;
      day.byTier.set(bucket.tier, dayTier);
      dailyAccumulator.set(bucket.day, day);
    }
  }

  const totalTokens = uncachedInputTokens + cachedInputTokens + cacheCreationTokens + outputTokens;

  const tiers: TierTotals[] = [...tierAccumulator.entries()]
    .map(([tier, totals]) => ({
      tier,
      costGbp: totals.costGbp,
      totalTokens: totals.totalTokens,
      records: totals.records,
      costShare: costGbp === 0 ? 0 : totals.costGbp / costGbp,
      tokenShare: totalTokens === 0 ? 0 : totals.totalTokens / totalTokens,
    }))
    .sort((a, b) => b.costGbp - a.costGbp);

  const models: ModelTotals[] = [...modelAccumulator.entries()]
    .map(([key, totals]) => ({
      model: (JSON.parse(key) as [string, string])[1],
      tier: totals.tier,
      costGbp: totals.costGbp,
      totalTokens: totals.totalTokens,
      records: totals.records,
      costShare: costGbp === 0 ? 0 : totals.costGbp / costGbp,
    }))
    .sort((a, b) => b.costGbp - a.costGbp || b.totalTokens - a.totalTokens);

  const agents: AgentTotals[] = [...agentAccumulator.entries()]
    .map(([agent, totals]) => ({
      agent,
      costGbp: totals.costGbp,
      totalTokens: totals.totalTokens,
      records: totals.records,
      costShare: costGbp === 0 ? 0 : totals.costGbp / costGbp,
    }))
    .sort((a, b) => b.costGbp - a.costGbp || b.totalTokens - a.totalTokens);

  const daily: DailyTotals[] = [...dailyAccumulator.entries()]
    .map(([day, totals]) => ({
      day,
      costGbp: totals.costGbp,
      totalTokens: totals.totalTokens,
      byTier: totals.byTier,
    }))
    .sort((a, b) => a.day.localeCompare(b.day));

  return {
    costGbp,
    uncachedInputTokens,
    cachedInputTokens,
    cacheCreationTokens,
    outputTokens,
    reasoningTokens,
    totalTokens,
    records,
    sessions,
    tiers,
    models,
    agents,
    daily,
    costQuality: {
      gatewayBilledShare: records === 0 ? 0 : gatewayBilledRecords / records,
      unpricedShare: records === 0 ? 0 : unpricedRecords / records,
      modelPricedShare:
        records === 0 ? 0 : (records - gatewayBilledRecords - unpricedRecords) / records,
      cacheSavingsGbp,
    },
    duplicateSources: duplicates,
    contributingEnvironments,
    staleEnvironments,
  };
}
